import classNames from 'classnames';
import React from 'react';
import PageLayout from '../app/layout/PageLayout';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import useAppSelector from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import SVGIcon from '../components/SVGIcon';
import HelpMenu from '../help/HelpMenu';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import groupIcon from '../icons/prefilled/16x16/group.svg';
import homeIcon from '../icons/prefilled/16x16/home.svg';
import NotificationButton from '../notification/NotificationButton';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import userPath from '../utils/url/userPath';
import useCanScrollUp from '../utils/useCanScrollUp';
import usePreventBodyScrolling from '../utils/usePreventBodyScrolling';
import * as styles from './Sidebar.module.scss';
import SidebarRow from './SidebarRow';
import SidebarSections from './SidebarSections';
import SidebarSectionsModal, {
  SIDEBAR_SECTIONS_CLICK_EVENTS,
} from './SidebarSectionsModal';
import SidebarSectionsProvider from './SidebarSectionsProvider';
import TeamButton from './TeamButton';

const USER_AVATAR_SIZE = parseInt(styles.userAvatarSize, 10);

const Sidebar = () => {
  const { isSidebarOpen } = useSidebarOpenState();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const currentUser = useAppSelector((state) => state.session.user);

  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const isGuest = currentUser.type === 'GUEST';

  usePreventBodyScrolling(isSidebarOpen && isMobile);
  const { setScrollingContainer, canScrollUp } = useCanScrollUp();

  return (
    <SidebarSectionsProvider>
      <PageLayout.Sidebar
        className={classNames(styles.sidebar, {
          [styles.scrolled]: canScrollUp,
          [styles.isSidebarSectionsModalOpen]: selectedSectionId != null,
        })}
        aria-label="Sidebar"
      >
        <header className={styles.teamNavRow}>
          <div className={styles.teamNavRowInner}>
            <TeamButton />
            <NotificationButton />
          </div>
        </header>
        <div ref={setScrollingContainer} className={styles.navs}>
          <div className={styles.mainNavs} aria-label="Site Navigation">
            <SidebarRow
              icon={
                <span className={styles.userAvatar}>
                  <Avatar size={USER_AVATAR_SIZE} user={currentUser} />
                </span>
              }
              to={userPath(currentUser)}
              onlyActiveOnIndex
            >
              {currentUser.name}
            </SidebarRow>

            <SidebarRow icon={homeIcon} to="/" onlyActiveOnIndex>
              Home
            </SidebarRow>

            <SidebarRow
              icon={
                <SVGIcon className={styles.draftsIcon}>{draftsIcon}</SVGIcon>
              }
              to="/drafts"
              onlyActiveOnIndex
            >
              Drafts
            </SidebarRow>

            {!isGuest && !isMobile && (
              <SidebarRow
                icon={
                  <SVGIcon className={styles.contentMapIcon}>
                    {subtopicsIcon}
                  </SVGIcon>
                }
                to="/topics"
                onlyActiveOnIndex
              >
                Content Map
              </SidebarRow>
            )}

            <SidebarRow icon={groupIcon} to="/groups">
              Groups
            </SidebarRow>
          </div>
          <div
            onMouseDown={(e) => {
              SIDEBAR_SECTIONS_CLICK_EVENTS.add(e.nativeEvent);
            }}
          >
            <SidebarSections />
          </div>
        </div>
        <HelpMenu appearance="internal" />
      </PageLayout.Sidebar>
      <SidebarSectionsModal />
    </SidebarSectionsProvider>
  );
};

export default Sidebar;
